import { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  Fieldset,
  InputGroup,
  Input,
  SimpleGrid,
} from "@chakra-ui/react";
import CountrySelect from "@/components/fields/CountrySelect";
import CurrencySelect from "@/components/fields/CurrencySelect";
import { countries } from "@/data/Countries";
import { countryCurrencies, currencyForCountry } from "@/data/CountryCurrencies";
import { DEFAULT_LOCATION, useLocationDefaults } from "@/hooks/useLocationDefaults";
import { SubmitHandler, useForm } from "react-hook-form";
import { Field } from "@/components/ui/field";
import { CompanyIcon, EmailIcon, LockIcon } from "@/components/icons/Icon";
import { PasswordInput } from "@/components/ui/password-input";
import PasswordStrengthIndicator from "@/components/PasswordStrengthIndicator";
import SubmitButton from "@/components/buttons/SubmitButton";
import { CaptchaInput } from "@/components/fields/CaptchaInput";
import { FormInput } from "@/components/fields/FormInput";
import { useI18n } from "src/i18n/I18nContext";
import { useNavigate } from "react-router-dom";
import { organizationService } from "src/service/OrganizationService";
import { userService } from "src/service/UserService";
import { useAuthContext } from "src/auth/AuthContext";
import WebUser from "src/auth/WebUser";
import { trackGoogleAnalyticsEvent } from "src/analytics/googleAnalytics";

interface SignupEntry {
  company: string;
  currency: string;
  country: string;
  city: string;
  email: string;
  firstName: string;
  lastName: string;
  newPassword: string;
  confirmNewPassword: string;
  captcha: string;
}

const SignupMarketingWidget = ({ t }: { t: (key: string, params?: Record<string, string | number>) => string }) => (
  <Box mb={{ base: "5", lg: "6" }}>
    <Heading as="h1" fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" lineHeight="short" mb="2" color="fg">
      {t('signup.headline')}{" "}
      <Text as="span" color="blue.600">{t('signup.headlineAccent')}</Text>
    </Heading>
    <Text fontSize="md" color="fg.muted" whiteSpace={{ base: "normal", md: "nowrap" }}>
      {t('signup.intro')}
    </Text>
  </Box>
);

export default function Signup() {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const { login, logout } = useAuthContext();
  const {
    register,
    control,
    getValues,
    setValue,
    handleSubmit,
    resetField,
    watch,
    formState: { errors },
  } = useForm<SignupEntry>({
    defaultValues: {
      company: '',
      currency: 'CHF',
      country: DEFAULT_LOCATION.country,
      city: DEFAULT_LOCATION.city,
      email: '',
      newPassword: '',
      confirmNewPassword: '',
      captcha: '',
    }
  });

  const edited = useRef({ country: false, currency: false, city: false });
  const { location, ready } = useLocationDefaults('public');

  useEffect(() => {
    if (!ready || edited.current.country) return;
    setValue('country', location.country);
    if (!edited.current.city) setValue('city', location.city);
    const currency = currencyForCountry(location.country);
    if (!edited.current.currency && currency) setValue('currency', currency);
  }, [ready, location, setValue]);

  const [num1, setNum1] = useState(3);
  const [num2, setNum2] = useState(7);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const generateCaptcha = () => {
    setNum1(Math.floor(Math.random() * 20) + 1);
    setNum2(Math.floor(Math.random() * 20) + 1);
    resetField("captcha");
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const onSubmitSignup: SubmitHandler<SignupEntry> = (entry) => {
    setIsSubmitting(true);
    setError('');
    organizationService
      .signup(entry.company, entry.email, entry.newPassword, entry.firstName, entry.lastName, entry.captcha, {
        currency: entry.currency, country: entry.country, city: entry.city,
      })
      .then((r) => {
        // The signup API has confirmed that the account was created. Track it
        // before the follow-up profile request, which is not part of signup.
        trackGoogleAnalyticsEvent("sign_up", { method: "email" });
        return userService.getMe(r.token).then((me) => {
          const webUser: WebUser = { token: r.token, data: me };
          login(webUser);
          navigate('/');
        });
      })
      .catch((e) => {
        console.error(e?.message ?? e);
        logout();
        setError(t('login.connectionError'));
        generateCaptcha();
        resetField("captcha");
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Container maxW="6xl" px={{ base: "4", md: "6" }} py={{ base: "3", md: "8" }} flex="1">
      <SignupMarketingWidget t={t} />
      <Box w="full" minW="0">
            <Card.Root
              bg="bg.panel"
              rounded="2xl"
              boxShadow="xl"
              borderWidth="1px"
              borderColor="border"
              p={{ base: "5", md: "8" }}
            >
              <Box mb="6">
                <Heading as="h2" fontSize="2xl" fontWeight="bold" mb="1" color="fg">
                  {t('signup.form.title')}
                </Heading>
                <Text color="fg.muted">{t('signup.form.subtitle')}</Text>
              </Box>

              <form noValidate onSubmit={handleSubmit(onSubmitSignup)}>
                <VStack gap="5" align="stretch">
                  <Fieldset.Root invalid={error.length > 0}>
                    <Fieldset.Content>
                      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: "5", lg: "6" }} alignItems="start">
                      <Box bg="blue.100/60" borderWidth="1px" borderColor="blue.100" _dark={{ bg: "blue.900/40", borderColor: "blue.900" }} rounded="xl" p={{ base: "4", md: "5" }}
                        css={{ "& input:not([role='combobox'])": { bg: "bg.panel" } }}>
                        <Text fontWeight="semibold" mb="1">{t('signup.form.organizationSection')}</Text>
                        <VStack align="stretch" gap="4">
                      <FormInput
                        name="company"
                        label={t('signup.form.company')}
                        register={register}
                        error={errors.company}
                        required
                        icon={CompanyIcon}
                        autoFocus={true}
                        placeholder={t('signup.form.companyPlaceholder')}
                        rules={{ required: t('signup.form.companyRequired') }}
                      />

                        <SimpleGrid columns={{ base: 1, sm: 2 }} gap="4" w="full">
                          <CountrySelect
                            name="country" control={control} readOnly={false}
                            countryCodes={Object.keys(countries)} language={language}
                            label={t('signup.form.country')} requiredMessage={t('signup.form.countryRequired')}
                            placeholder={t('signup.form.country')} inputId="signup-country"
                            aria-label={t('signup.form.country')} isClearable={false}
                            chakraStyles={{ control: provided => ({ ...provided, bg: 'bg.panel' }) }}
                            onValueChange={country => {
                              edited.current.country = true;
                              if (country !== getValues('country')) setValue('city', '', { shouldDirty: true });
                              const currency = currencyForCountry(country);
                              if (currency && !edited.current.currency) setValue('currency', currency, { shouldValidate: true });
                            }}
                          />
                          <CurrencySelect
                            name="currency" control={control} readOnly={false}
                            currencyCodes={Object.values(countryCurrencies)} language={language}
                            label={t('signup.form.currency')} requiredMessage={t('signup.form.currencyRequired')}
                            placeholder={t('signup.form.currency')} inputId="signup-currency"
                            aria-label={t('signup.form.currency')} isSearchable isClearable={false}
                            chakraStyles={{ control: provided => ({ ...provided, bg: 'bg.panel' }) }}
                            onValueChange={() => { edited.current.currency = true; }}
                          />
                        </SimpleGrid>
                        <Field label={t('signup.form.city')} optionalText={<Text as="span" ml="1" color="fg.muted" fontWeight="normal">{t('signup.form.optional')}</Text>}>
                          <Input placeholder={t('signup.form.cityPlaceholder')} autoComplete="address-level2"
                            {...register('city', { onChange: () => { edited.current.city = true; } })} />
                        </Field>
                        <Text fontSize="sm" color="fg.muted">{t('signup.form.locationHint')}</Text>
                        </VStack>
                      </Box>
                      <Box p={{ base: "0", lg: "5" }} minW="0">
                        <Text fontWeight="semibold" mb="1">{t('signup.form.accountSection')}</Text>
                        <Text fontSize="sm" color="fg.muted" mb="4">{t('signup.form.accountHint')}</Text>
                        <VStack align="stretch" gap="4">
                      <FormInput
                        name="email"
                        label={t('signup.form.email')}
                        register={register}
                        error={errors.email}
                        required
                        icon={EmailIcon}
                        placeholder={t('signup.form.emailPlaceholder')}
                        type="email"
                        rules={{ required: t('signup.form.emailRequired') }}
                      />

                      <Flex gap="4" align="flex-start" direction={{ base: "column", sm: "row" }}>
                        <Box flex="1" width='full'>
                          <FormInput
                            name="firstName"
                            label={t('signup.form.firstName')}
                            register={register}
                            error={errors.firstName}
                            required
                            placeholder={t('signup.form.firstNamePlaceholder')}
                            rules={{ required: t('signup.form.firstNameRequired') }}
                          />
                        </Box>
                        <Box flex="1" width='full'>
                          <FormInput
                            name="lastName"
                            label={t('signup.form.lastName')}
                            register={register}
                            error={errors.lastName}
                            required
                            placeholder={t('signup.form.lastNamePlaceholder')}
                            rules={{ required: t('signup.form.lastNameRequired') }}
                          />
                        </Box>
                      </Flex>

                      <Flex gap="4" align="flex-start" direction={{ base: "column", sm: "row" }}>
                        <Field id="newPassword" label={t('signup.form.newPassword')} required invalid={!!errors.newPassword} errorText={errors.newPassword?.message}>
                          <InputGroup startElement={<LockIcon />}>
                            <PasswordInput {...register('newPassword', { required: t('signup.form.passwordRequired') })} />
                          </InputGroup>
                        </Field>
                        <Field id="confirmNewPassword" label={t('signup.form.confirmPassword')} required invalid={!!errors.confirmNewPassword} errorText={errors.confirmNewPassword?.message}>
                          <InputGroup startElement={<LockIcon />}>
                            <PasswordInput {...register('confirmNewPassword', {
                              required: t('signup.form.confirmRequired'),
                              validate: (value) => value === watch('newPassword') || t('signup.form.passwordMismatch')
                            })} />
                          </InputGroup>
                        </Field>
                      </Flex>
                      <PasswordStrengthIndicator password={watch('newPassword')} shortScoreWord={t('signup.form.short')} scoreWords={[t('signup.form.weak'), t('signup.form.weak'), t('signup.form.okay'), t('signup.form.good'), t('signup.form.strong')]} />

                        </VStack>
                      </Box>
                      </SimpleGrid>

                      <Flex direction={{ base: "column", lg: "row" }} gap={{ base: "4", lg: "6" }} align={{ base: "stretch", lg: "center" }} borderTopWidth="1px" borderColor="border" pt="5">
                        <Box flex="1" minW="0">
                      <CaptchaInput
                        leftOperand={num1}
                        rightOperand={num2}
                        onRegenerate={generateCaptcha}
                        invalid={!!errors.captcha}
                        errorText={errors.captcha?.message}
                        {...register("captcha", {
                          required: t('signup.form.captchaRequired'),
                          validate: (value) =>
                            parseInt(value, 10) === num1 + num2 || t('signup.form.captchaInvalid'),
                        })}
                      />

                        </Box>
                        <Box w={{ base: "full", lg: "280px" }} flexShrink="0">
                          <SubmitButton caption={t('signup.form.submit')} isSubmitting={isSubmitting} disabled={isSubmitting} w="full" minH="12" />
                        </Box>
                      </Flex>
                    </Fieldset.Content>
                    <Fieldset.ErrorText>
                      {error}
                    </Fieldset.ErrorText>
                  </Fieldset.Root>
                </VStack>
              </form>

              <Box mt="5" pt="4" borderTopWidth="1px" borderColor="border">
                <Text fontSize="sm" color="fg.muted" textAlign="center">
                  {t('signup.form.emailPrivacyNotice')}
                </Text>
              </Box>
            </Card.Root>
      </Box>
    </Container>
  );
}
